import type { JsonRpcToolCallRequest } from '../../mcp/McpBridgeProvider';
import { toolResultToMarkdown } from '../../mcp/mcpBridgeUtils';
import { ToolOutcome } from '../../types/ClientMessage';

/**
 * The chat tool-call loop as a pure state machine.
 *
 * Mirrors the reducer-driven architecture of `useMultipartDownloadUpload`: every
 * async event (chat request settled, host ACK/RESPONSE, timeout, user
 * approve/reject) is expressed as an ACTION; the reducer is the single source of
 * truth for "what is the loop doing"; and a driver effect in `useChat` reads the
 * derived state and performs the side-effects (send a chat turn, postMessage a
 * tool call, arm timeouts). Nothing in this file touches React, the network or
 * `postMessage` — it is exhaustively unit-testable on its own.
 */

/** A finished tool call rendered as a backend-bound `role: 'tool'` chat turn. */
export type ToolResultTurn = {
  id: string | number;
  query: string;
  outcome: ToolOutcome;
  approvedByUser: boolean;
};

export enum ToolCallStatus {
  /** Non-readonly tool — waiting for the user to approve or reject. */
  AwaitingAuthorization = 'awaiting_authorization',
  /** Approved (or readonly) — the driver will postMessage it on the next tick. */
  Ready = 'ready',
  /** Sent to the host — waiting for MCP_ACK. */
  Dispatched = 'dispatched',
  /** ACK received — waiting for MCP_RESPONSE. */
  Acknowledged = 'acknowledged',
}

export type ToolCall = {
  id: string | number;
  name: string;
  request: JsonRpcToolCallRequest;
  status: ToolCallStatus;
  requiresAuthorization: boolean;
  /** position within the turn's batch; the driver runs calls in ascending order. */
  order: number;
  /** epoch ms when the call entered `Dispatched` (for timeout accounting). */
  dispatchedAt: number | null;
};

export type ChatState = {
  /** Tool calls currently in flight, keyed by JSON-RPC id. */
  toolCalls: Record<string | number, ToolCall>;
  /** Finished tool calls queued to send back to the backend (FIFO). */
  outbox: ToolResultTurn[];
  /** True while a chat request (user message or tool-result turn) is in flight. */
  sending: boolean;
};

export const initialChatState: ChatState = {
  toolCalls: {},
  outbox: [],
  sending: false,
};

export enum ChatActionType {
  SendStarted = 'send_started',
  SendSettled = 'send_settled',
  ToolRequested = 'tool_requested',
  ToolAuthorized = 'tool_authorized',
  ToolRejected = 'tool_rejected',
  ToolDispatched = 'tool_dispatched',
  ToolAcknowledged = 'tool_acknowledged',
  ToolResponded = 'tool_responded',
  ToolTimedOut = 'tool_timed_out',
  ToolResultsSent = 'tool_results_sent',
  Reset = 'reset',
}

export type ChatAction =
  | { type: ChatActionType.SendStarted }
  | { type: ChatActionType.SendSettled }
  | {
      type: ChatActionType.ToolRequested;
      request: JsonRpcToolCallRequest;
      requiresAuthorization: boolean;
      /** position within this turn's batch; drives sequential run order. */
      order: number;
    }
  | { type: ChatActionType.ToolAuthorized; id: string | number }
  | { type: ChatActionType.ToolRejected; id: string | number }
  | { type: ChatActionType.ToolDispatched; id: string | number; at: number }
  | { type: ChatActionType.ToolAcknowledged; id: string | number }
  | { type: ChatActionType.ToolResponded; id: string | number; result?: unknown; error?: unknown }
  | { type: ChatActionType.ToolTimedOut; id: string | number }
  | { type: ChatActionType.ToolResultsSent; ids: (string | number)[] }
  | { type: ChatActionType.Reset };

// --- helpers --------------------------------------------------------------

const withoutCall = (toolCalls: ChatState['toolCalls'], id: string | number) => {
  const { [id]: _removed, ...rest } = toolCalls;
  return rest;
};

const queueResult = (state: ChatState, call: ToolCall, query: string, outcome: ToolOutcome): ChatState => ({
  ...state,
  toolCalls: withoutCall(state.toolCalls, call.id),
  outbox: [
    ...state.outbox,
    {
      id: call.id,
      query,
      outcome,
      approvedByUser: call.requiresAuthorization && (outcome === ToolOutcome.Done || outcome === ToolOutcome.Failed),
    },
  ],
});

const byOrder = (calls: ToolCall[]) => [...calls].sort((a, b) => a.order - b.order);

const isToolError = (result: unknown) =>
  typeof result === 'object' && result !== null && (result as { isError?: unknown }).isError === true;

// --- reducer --------------------------------------------------------------

export const chatReducer = (state: ChatState, action: ChatAction): ChatState => {
  switch (action.type) {
    case ChatActionType.SendStarted:
      return { ...state, sending: true };

    case ChatActionType.SendSettled:
      return { ...state, sending: false };

    case ChatActionType.ToolRequested: {
      const { request, requiresAuthorization, order } = action;
      return {
        ...state,
        toolCalls: {
          ...state.toolCalls,
          [request.id]: {
            id: request.id,
            name: request.params.name,
            request,
            status: requiresAuthorization ? ToolCallStatus.AwaitingAuthorization : ToolCallStatus.Ready,
            requiresAuthorization,
            order,
            dispatchedAt: null,
          },
        },
      };
    }

    // Approval is granted per tool type: approving a call approves every call of the same
    // tool still awaiting in the turn, so a batch of N is one decision, not N.
    case ChatActionType.ToolAuthorized: {
      const call = state.toolCalls[action.id];
      if (!call || call.status !== ToolCallStatus.AwaitingAuthorization) return state;
      const toolCalls = { ...state.toolCalls };
      for (const other of Object.values(state.toolCalls)) {
        if (other.name === call.name && other.status === ToolCallStatus.AwaitingAuthorization) {
          toolCalls[other.id] = { ...other, status: ToolCallStatus.Ready };
        }
      }
      return { ...state, toolCalls };
    }

    // A denial halts the turn: the denied call and every call still queued behind it are
    // retired unrun, and all their results go back to the backend together.
    case ChatActionType.ToolRejected: {
      const denied = state.toolCalls[action.id];
      if (!denied) return state;
      return byOrder(Object.values(state.toolCalls)).reduce((next, call) => {
        const isDenied = call.id === denied.id || call.name === denied.name;
        const error = isDenied
          ? `I reject the usage of the tool ${call.name}`
          : `Not executed: I rejected the usage of the tool ${denied.name} earlier in this turn`;
        const query = toolResultToMarkdown({ id: call.id, name: call.name, error });
        return queueResult(next, call, query, isDenied ? ToolOutcome.Denied : ToolOutcome.Skipped);
      }, state);
    }

    case ChatActionType.ToolDispatched: {
      const call = state.toolCalls[action.id];
      if (!call || call.status !== ToolCallStatus.Ready) return state;
      return {
        ...state,
        toolCalls: {
          ...state.toolCalls,
          [action.id]: { ...call, status: ToolCallStatus.Dispatched, dispatchedAt: action.at },
        },
      };
    }

    case ChatActionType.ToolAcknowledged: {
      const call = state.toolCalls[action.id];
      if (!call || call.status !== ToolCallStatus.Dispatched) return state;
      return {
        ...state,
        toolCalls: { ...state.toolCalls, [action.id]: { ...call, status: ToolCallStatus.Acknowledged } },
      };
    }

    case ChatActionType.ToolResponded: {
      const call = state.toolCalls[action.id];
      // Ignore unknown ids: a timeout may have already retired this call.
      if (!call) return state;
      const query = toolResultToMarkdown({
        id: action.id,
        name: call.name,
        result: action.result,
        error: action.error as string | undefined,
      });
      const failed = Boolean(action.error) || isToolError(action.result);
      return queueResult(state, call, query, failed ? ToolOutcome.Failed : ToolOutcome.Done);
    }

    case ChatActionType.ToolTimedOut: {
      const call = state.toolCalls[action.id];
      if (!call) return state;
      const query = toolResultToMarkdown({
        id: action.id,
        name: call.name,
        error: 'MCP tool is unresponsive',
      });
      return queueResult(state, call, query, ToolOutcome.Failed);
    }

    case ChatActionType.ToolResultsSent:
      return { ...state, outbox: state.outbox.filter((turn) => !action.ids.includes(turn.id)) };

    case ChatActionType.Reset:
      return initialChatState;

    default:
      return state;
  }
};

// --- selectors ------------------------------------------------------------

/** The tool currently being processed: the lowest-`order` call still in the
 * loop. The turn's calls are approved and run strictly one at a time in this
 * order — a call becomes active only once every earlier call has settled. */
export const selectActiveCall = (state: ChatState): ToolCall | undefined => byOrder(Object.values(state.toolCalls))[0];

/** The active call's request when it is waiting on the user's decision — the
 * single tool the approval prompt asks about right now, or null. */
export const selectActiveAuthorizationRequest = (state: ChatState): JsonRpcToolCallRequest | null => {
  const active = selectActiveCall(state);
  return active?.status === ToolCallStatus.AwaitingAuthorization ? active.request : null;
};

/** The calls the current approval covers: every call of the active call's tool still
 * awaiting in the turn, or none when the active call needs no decision. */
export const selectActiveApprovalCalls = (state: ChatState): ToolCall[] => {
  const active = selectActiveCall(state);
  if (active?.status !== ToolCallStatus.AwaitingAuthorization) return [];
  return byOrder(Object.values(state.toolCalls)).filter(
    (call) => call.name === active.name && call.status === ToolCallStatus.AwaitingAuthorization,
  );
};

/** Calls that have been sent to the host (dispatched or acknowledged). */
export const selectInFlightCount = (state: ChatState): number =>
  Object.values(state.toolCalls).filter(
    (call) => call.status === ToolCallStatus.Dispatched || call.status === ToolCallStatus.Acknowledged,
  ).length;

/** True while the assistant is "working": sending, or a tool turn is pending. */
export const selectIsBusy = (state: ChatState): boolean =>
  state.sending || state.outbox.length > 0 || Object.keys(state.toolCalls).length > 0;
